import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { registerForPush, unregisterPush } from '../utils/push';
import api from '../utils/api';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const cached = localStorage.getItem('user');
        if (!cached) return null;
        try {
            const parsed = JSON.parse(cached);
            // اگر کاربر غیرفعال بود، خروج
            if (parsed && parsed.isActive === false) {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                return null;
            }
            return parsed;
        } catch (e) {
            return null;
        }
    });
    const [loading, setLoading] = useState(true);
    const inFlightRef = useRef(null);
    const lastFetchRef = useRef(0);
    const userRef = useRef(user);

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    const refreshUser = useCallback(async (options = {}) => {
        const { force = false } = options;
        const token = localStorage.getItem('token');
        if (!token) {
            setUser(null);
            return null;
        }
        const now = Date.now();
        if (!force && inFlightRef.current) {
            return inFlightRef.current;
        }
        if (!force && now - lastFetchRef.current < 30000) {
            return userRef.current;
        }
        try {
            const request = api.get('/users/me');
            inFlightRef.current = request;
            const response = await request;
            inFlightRef.current = null;
            lastFetchRef.current = Date.now();
            // اگر کاربر غیرفعال بود، خروج
            if (response.data && response.data.isActive === false) {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                setUser(null);
                return null;
            }
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            return response.data;
        } catch (e) {
            inFlightRef.current = null;
            console.error('Failed to fetch user from API', e);
            const status = e?.response?.status;
            if (status === 401 || status === 403) {
                localStorage.removeItem('user');
                setUser(null);
                return null;
            }
            const cached = localStorage.getItem('user');
            if (cached) {
                try {
                    const parsed = JSON.parse(cached);
                    setUser(parsed);
                    return parsed;
                } catch (err) {
                    setUser(null);
                }
            }
            return null;
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                await refreshUser();
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    const login = async (userData, token, refreshToken) => {
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshToken);
        setUser(userData);
        await refreshUser();
        try { registerForPush(); } catch (e) { console.warn('push register failed', e); }
    };

    const logout = async () => {
        // ابطال رفرش‌توکن سمت سرور (در صورت شکست، خروج محلی همچنان انجام می‌شود)
        try { await api.post('/auth/logout'); } catch (e) { console.warn('server logout failed', e); }
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setUser(null);
        try { unregisterPush(); } catch (e) { console.warn('push unregister failed', e); }
    };

    const updateUser = (userData) => {
        const updatedUser = { ...user, ...userData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
    };

    return (
        <UserContext.Provider value={{ user, loading, login, logout, updateUser, refreshUser }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
